/**
 * Utility for calculating differences between objects
 * Used for version comparison in agent/tool/execution hook settings
 */
import stringDiffUtils from "./stringDiffUtils";

// Field label mappings for human-readable names
const FIELD_LABELS = {
// Agent Settings
  llmBaseModel: "LLM Base Model",
  functionCallingModel: "Function Calling Model",
  visionModel: "Vision Model",
  adverserialModel: "Adversarial Model",
  agenticCodingModel: "Agentic Coding Model",
  temperature: "Temperature",
  maxTokens: "Max Tokens",
  mode: "Agent Mode",
  systemPrompt: "System Prompt",
  predictedPlanJson: "Workflow Plan",
  allowedTopics: "Knowledge Topics",
  staticDocs: "Static Documents",
  allowedFunctions: "Allowed Functions",
  agentsPool: "Agents Pool",
  tools: "Tools Configuration",
  hasCode: "Code Interpreter",
  hasImages: "Image Support",
  hasFunctions: "Function Calling",
  hasTopicsContext: "Knowledge Context",
  allowImagesGeneration: "Image Generation",
  allowVideosGeneration: "Video Generation",
  allowInternetSearch: "Internet Search",
  allowAskQuestion: "Ask User Question",
  agenticRAG: "Agentic RAG",
  charting: "Charting",
  reasoningEffort: "Reasoning Effort",
  fnReasoningEffort: "Function Calling Reasoning",
  llmProvider: "LLM Provider",
  functionCallingProvider: "Function Calling Provider",
  adverserialProvider: "Adversarial Provider",
  icon: "Agent Icon",
  largeLogo: "Large Logo",
  voiceFileKey: "Voice File",
  voiceName: "Voice Name",
  ttsModel: "TTS Model",
  sttModel: "STT Model",
  defaultVoiceLanguage: "Voice Language",
  isMultiLanguageEnabled: "Multi-Language Support",
  advancedLanguageDetection: "Advanced Language Detection",
  plainTextOutput: "Plain Text Output",
  restrictedAccess: "Restricted Access",
  preventWidgetUsage: "Prevent Widget Usage",
  allowedRoles: "Allowed Roles",
  showReferencesToInternalDocs: "Show References",
  blender: "Blender",
  agentDesktopVM: "Desktop VM",
  vms: "VM Selection",
  disclaimer: "Disclaimer",
  showDocumentsReferences: "Show Document References",
  hasMemory: "Memory Enabled",
  defaultAnswer: "Default Answer",
  noKnowledgeDefaultAnswer: "No Knowledge Default Answer",
  quickQuestions: "Quick Questions",
  stopSequence: "Stop Sequence",
  examples: "Examples",
  goals: "Goals",
  topK: "Top K Results",
  docTopK: "Document Top K",
  minRetrievalScore: "Minimum Retrieval Score",
  maxHistory: "Max History",
  promptTopKeywords: "Prompt Top Keywords",
  keyWordsForKnowledgeBase: "Knowledge Base Keywords",
  topicEnhancer: "Topic Enhancer",
  maxTopics: "Max Topics",
  timeSignificance: "Time Significance",
  recencyImportance: "Recency Importance",
  maxRecencyIncrease: "Max Recency Increase",
  recencyTopKIncreaseFactor: "Recency Top K Increase Factor",
  enhancementPassage: "Enhancement Passage",
  inhibitionPassage: "Inhibition Passage",
  pertinencePassage: "Pertinence Passage",
  summarisation: "Summarisation",
  compressor: "Compressor",
  hasModeration: "Moderation Enabled",
  customModerations: "Custom Moderations",
  moderationMessage: "Moderation Message",
  languageDetectionConfidence: "Language Detection Confidence",
  showCodeInExecution: "Show Code in Execution",
  hideReasoning: "Hide Reasoning",
  messageOnLaunch: "Launch Message",
  messagesOnLaunch: "Launch Messages",
  rotateMessagesOnLaunch: "Rotate Launch Messages",
  rotateMessagesOnLaunchInterval: "Rotation Interval",
  placeholderMessageOnLoading: "Loading Placeholder",
  dynamicPlaceholderMessageOnLoading: "Dynamic Loading Placeholder",
  placeholderInputMessage: "Input Placeholder",
  openByDefault: "Open by Default",
  interpolationString: "Interpolation String",
  
  // Agent Instructions
  customGreetingInstructions: "Greeting Instructions",
  customConclusionInstructions: "Conclusion Instructions",
  customChartingInstructions: "Charting Instructions",
  customSummarisationInstructions: "Summarisation Instructions",
  customImagesInstructions: "Image Instructions",
  customToolingInstructions: "Tooling Instructions",
  planningInstructions: "Planning Instructions",
  virtualDesktopInstructions: "Virtual Desktop Instructions",
  
  // Execution Hook Settings
  name: "Name",
  description: "Description",
  customExecutionCode: "Code Snippet",
  customExecutionInstructions: "Instructions",
  customExecutionSecrets: "Authorisations & Secrets",
  customExecutionEnvironment: "Execution Environment",
  codeExecutionEnvironmentID: "Environment ID",
  functionName: "Function Name",
  hardcodedScript: "Execute as Static Script",
  isTemplate: "Execute as Python Tool",
  
  // Tool Settings
  script: "Script",
  authorisationID: "Authorisation",
  dbConnectionID: "Database Connection",
  isChatFn: "Chat Function",
  isAgentSkill: "Agent Skill",
  
  // Common fields
  label: "Label",
  title: "Title",
};

// Field categories for grouping changes
const FIELD_CATEGORIES = {
  "Model Settings": [
    "llmBaseModel",
    "functionCallingModel",
    "visionModel",
    "adverserialModel",
    "agenticCodingModel",
    "temperature",
    "maxTokens",
    "llmProvider",
    "functionCallingProvider",
    "adverserialProvider",
    "reasoningEffort",
    "fnReasoningEffort",
  ],
  "Tools & Capabilities": [
    "hasCode",
    "hasImages",
    "hasFunctions",
    "allowImagesGeneration",
    "allowVideosGeneration",
    "allowInternetSearch",
    "allowAskQuestion",
    "agenticRAG",
    "charting",
    "tools",
    "showCodeInExecution",
    "hideReasoning",
  ],
  "Knowledge": [
    "allowedTopics",
    "staticDocs",
    "hasTopicsContext",
    "allowedFunctions",
    "showReferencesToInternalDocs",
    "showDocumentsReferences",
    "blender",
    "topK",
    "docTopK",
    "minRetrievalScore",
    "maxTopics",
    "topicEnhancer",
    "promptTopKeywords",
    "keyWordsForKnowledgeBase",
  ],
  "Behavior": [
    "mode",
    "systemPrompt",
    "predictedPlanJson",
    "plainTextOutput",
    "agentsPool",
    "customGreetingInstructions",
    "customConclusionInstructions",
    "customChartingInstructions",
    "customSummarisationInstructions",
    "customImagesInstructions",
    "customToolingInstructions",
    "planningInstructions",
    "virtualDesktopInstructions",
    "disclaimer",
    "defaultAnswer",
    "noKnowledgeDefaultAnswer",
    "stopSequence",
    "examples",
    "goals",
  ],
  "Memory & History": [
    "hasMemory",
    "maxHistory",
    "timeSignificance",
    "recencyImportance",
    "maxRecencyIncrease",
    "recencyTopKIncreaseFactor",
  ],
  "Moderation": [
    "hasModeration",
    "customModerations",
    "moderationMessage",
  ],
  "Voice & Language": [
    "voiceFileKey",
    "voiceName",
    "ttsModel",
    "sttModel",
    "defaultVoiceLanguage",
    "isMultiLanguageEnabled",
    "advancedLanguageDetection",
    "languageDetectionConfidence",
  ],
  "Permissions": [
    "restrictedAccess",
    "preventWidgetUsage",
    "allowedRoles",
  ],
  "Identity": [
    "label",
    "title",
    "description",
    "icon",
    "largeLogo",
    "name",
  ],
  "User Experience": [
    "quickQuestions",
    "messageOnLaunch",
    "messagesOnLaunch",
    "rotateMessagesOnLaunch",
    "rotateMessagesOnLaunchInterval",
    "placeholderMessageOnLoading",
    "dynamicPlaceholderMessageOnLoading",
    "placeholderInputMessage",
    "openByDefault",
    "interpolationString",
  ],
  "Code Execution": [
    "customExecutionCode",
    "customExecutionInstructions",
    "customExecutionSecrets",
    "customExecutionEnvironment",
    "codeExecutionEnvironmentID",
    "functionName",
    "hardcodedScript",
    "isTemplate",
    "script",
    "authorisationID",
    "dbConnectionID",
    "isChatFn",
    "isAgentSkill",
  ],
  "Desktop": [
    "agentDesktopVM",
    "vms",
  ],
  "Summarisation": [
    "summarisation",
    "compressor",
    "enhancementPassage",
    "inhibitionPassage",
    "pertinencePassage",
  ],
};

/**
 * Determine the category for a field
 */
const getFieldCategory = (field) => {
  for (const [category, fields] of Object.entries(FIELD_CATEGORIES)) {
    if (fields.includes(field)) {
      return category;
    }
  }
  return "Other";
};

/**
 * Get human-readable label for a field
 */
const getFieldLabel = (field) => {
  return FIELD_LABELS[field] || field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
};

/**
 * Format a value for display in diff view
 * Handles truncation, type detection, and readability
 */
const formatValueForDisplay = (value, fieldName = null) => {
  if (value === null || value === undefined) {
    return "Not set";
  }
  
  if (typeof value === "boolean") {
    return value ? "Enabled" : "Disabled";
  }
  
  if (typeof value === "number") {
    return value.toString();
  }
  
  if (typeof value === "string") {
    // Check if it's JSON
    if (value.startsWith("{") || value.startsWith("[")) {
      try {
        const parsed = JSON.parse(value);
        return formatValueForDisplay(parsed, fieldName);
      } catch (e) {
        // Not JSON, treat as string
      }
    }
    
    // Truncate long strings
    if (value.length > 100) {
      return value.substring(0, 97) + "...";
    }
    
    return value;
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "Empty list";
    }
    
    // Check if array of objects with labels
    const displayItems = value.map((item) => {
      if (typeof item === "object" && item !== null) {
        return item.label || item.name || item.id || "Item";
      }
      return item;
    });
    
    if (displayItems.length <= 3) {
      return displayItems.join(", ");
    }
    
    return `${displayItems.length} items (${displayItems.slice(0, 2).join(", ")}, ...)`;
  }
  
  if (typeof value === "object") {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return "Empty object";
    }
    
    if (keys.length <= 3) {
      return `{${keys.join(", ")}}`;
    }
    
    return `{${keys.length} fields}`;
  }
  
  return String(value);
};

/**
 * Check if a value is considered "complex" (array or object)
 */
const isComplexValue = (value) => {
  if (typeof value === "string") {
    // Check if it's JSON
    if (value.startsWith("{") || value.startsWith("[")) {
      try {
        JSON.parse(value);
        return true;
      } catch (e) {
        return false;
      }
    }
  }
  
  return Array.isArray(value) || (typeof value === "object" && value !== null);
};

/**
 * Calculate differences between two objects
 * Returns detailed change information for display
 */
const calculateDifferences = (oldObj, newObj, options = {}) => {
  const {
    excludeFields = ["id", "createdAt", "updatedAt", "_version", "_deleted", "_lastChangedAt", "workspaceID"],
    showUnchanged = false,
  } = options;
  
  const changes = [];
  
  // Get all unique keys from both objects
  const allKeys = new Set([
    ...Object.keys(oldObj || {}),
    ...Object.keys(newObj || {}),
  ]);
  
  allKeys.forEach((key) => {
    // Skip excluded fields
    if (excludeFields.includes(key)) {
      return;
    }
    
    const oldValue = oldObj?.[key];
    const newValue = newObj?.[key];
    
    // Determine change type
    let changeType = null;
    
    if (oldValue === undefined || oldValue === null) {
      if (newValue !== undefined && newValue !== null) {
        changeType = "added";
      }
    } else if (newValue === undefined || newValue === null) {
      if (oldValue !== undefined && oldValue !== null) {
        changeType = "removed";
      }
    } else if (!areValuesEqual(oldValue, newValue)) {
      changeType = "modified";
    } else if (showUnchanged) {
      changeType = "unchanged";
    }
    
    if (changeType) {
      const change = {
        field: key,
        label: getFieldLabel(key),
        category: getFieldCategory(key),
        oldValue,
        newValue,
        changeType,
        isComplex: isComplexValue(oldValue) || isComplexValue(newValue),
        oldDisplay: formatValueForDisplay(oldValue, key),
        newDisplay: formatValueForDisplay(newValue, key),
      };

      if (changeType === "modified" && 
          typeof oldValue === "string" && 
          typeof newValue === "string" &&
          stringDiffUtils.shouldDiffAsString(oldValue) &&
          stringDiffUtils.shouldDiffAsString(newValue)) {
        change.stringDiff = stringDiffUtils.computeWordDiff(oldValue, newValue);
      }

      changes.push(change);
    }
  });
  
  // Group changes by category
  const groupedChanges = {};
  changes.forEach((change) => {
    const category = change.category;
    if (!groupedChanges[category]) {
      groupedChanges[category] = [];
    }
    groupedChanges[category].push(change);
  });
  
  // Sort changes within each category by label
  Object.keys(groupedChanges).forEach((category) => {
    groupedChanges[category].sort((a, b) => a.label.localeCompare(b.label));
  });
  
  return {
    totalChanges: changes.filter((c) => c.changeType !== "unchanged").length,
    changes,
    groupedChanges,
  };
};

/**
 * Compare two values for equality
 * Handles arrays, objects, and JSON strings
 */
const areValuesEqual = (val1, val2) => {
  // Both null/undefined
  if ((val1 === null || val1 === undefined) && (val2 === null || val2 === undefined)) {
    return true;
  }
  
  // One null/undefined
  if (val1 === null || val1 === undefined || val2 === null || val2 === undefined) {
    return false;
  }
  
  // Different types
  if (typeof val1 !== typeof val2) {
    return false;
  }
  
  // String comparison (including JSON strings)
  if (typeof val1 === "string") {
    // Try to parse both as JSON
    try {
      const parsed1 = JSON.parse(val1);
      const parsed2 = JSON.parse(val2);
      return areValuesEqual(parsed1, parsed2);
    } catch (e) {
      // Not JSON, compare as strings
      return val1 === val2;
    }
  }
  
  // Array comparison
  if (Array.isArray(val1) && Array.isArray(val2)) {
    if (val1.length !== val2.length) {
      return false;
    }
    
    // Compare array items (by id if objects, by value if primitives)
    const ids1 = val1.map((item) => (typeof item === "object" ? item?.id || item : item));
    const ids2 = val2.map((item) => (typeof item === "object" ? item?.id || item : item));
    
    return JSON.stringify(ids1.sort()) === JSON.stringify(ids2.sort());
  }
  
  // Object comparison
  if (typeof val1 === "object") {
    const keys1 = Object.keys(val1);
    const keys2 = Object.keys(val2);
    
    if (keys1.length !== keys2.length) {
      return false;
    }
    
    return keys1.every((key) => areValuesEqual(val1[key], val2[key]));
  }
  
  // Primitive comparison
  return val1 === val2;
};

/**
 * Group changes by category for better UX
 * Already done in calculateDifferences, but exported for flexibility
 */
const groupChangesByCategory = (changes) => {
  const grouped = {};
  changes.forEach((change) => {
    const category = change.category;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(change);
  });
  return grouped;
};

export default {
  calculateDifferences,
  formatValueForDisplay,
  groupChangesByCategory,
  getFieldLabel,
  getFieldCategory,
  isComplexValue,
  areValuesEqual,
  FIELD_LABELS,
  FIELD_CATEGORIES,
};